/**
 * ADR-0068: Console-side scope types + useMyGroups hook.
 *
 * Types mirror `atlas-hub/services/hub/src/contentScope/types.ts` — kept in
 * sync manually (spec §9.1). Keep the two files in lockstep when extending
 * either.
 */

import { useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "./api.js";

export type ContentScope = "personal" | "team" | "shared";

export type ScopedEntityKind = "page" | "menu" | "pdo" | "workflow" | "saved_query" | "study" | "optimize_model";

export type ScopeTab = "mine" | "teams" | "shared";

export interface ScopedEntityFields {
  scope: ContentScope;
  owner_account_id: string | null;
  owner_group_id: string | null;
  owner_group_name?: string | null;
  /** ADR-0173 §4.8 — owner attribution for the Personal pill; null for team/shared rows. */
  owner_display_name?: string | null;
  scope_changed_at: string | null;
  scope_changed_by: string | null;
}

export interface ScopeCounts {
  mine: number;
  teams: number;
  shared: number;
}

export interface MyGroup {
  id: string;
  name: string;
}

export interface ScopeReferenceViolation {
  ref_kind: string;
  ref_id: string;
  ref_name: string | null;
  ref_scope: ContentScope;
}

/** Human label for a scoped row. Matches spec §2 line 117. */
export function scopeLabel(row: Pick<ScopedEntityFields, "scope" | "owner_group_name">): string {
  if (row.scope === "personal") return "Personal";
  if (row.scope === "team") return row.owner_group_name ? `Team: ${row.owner_group_name}` : "Team";
  return "Shared";
}

/**
 * Module-local cache of /tenant/my-groups. React's `useMyGroups` below uses
 * this to avoid re-fetching on every mount. Cache is reset manually via
 * `invalidateMyGroups()` when a caller changes group membership.
 */
let myGroupsCache: MyGroup[] | null = null;
let myGroupsInFlight: Promise<MyGroup[]> | null = null;

/** Drop the cached groups list — call after any group-membership mutation. */
export function invalidateMyGroups(): void {
  myGroupsCache = null;
  myGroupsInFlight = null;
}

async function fetchMyGroups(): Promise<MyGroup[]> {
  if (myGroupsCache) return myGroupsCache;
  if (myGroupsInFlight) return myGroupsInFlight;
  myGroupsInFlight = (async () => {
    const r = await apiFetch<{ groups: MyGroup[] }>("/api/tenant/my-groups");
    myGroupsCache = r.groups ?? [];
    myGroupsInFlight = null;
    return myGroupsCache;
  })();
  return myGroupsInFlight;
}

/**
 * Hook: returns the caller's active group memberships. Fetches lazily on
 * first mount; subsequent consumers read from the module cache so repeated
 * mounts across a single session avoid N+1 requests.
 *
 * Call `invalidateMyGroups()` after any admin action that changes group
 * membership (e.g., a new team assignment) to force a refetch.
 */
export function useMyGroups(): { groups: MyGroup[]; loading: boolean; error: string | null } {
  const [state, setState] = useState<{ groups: MyGroup[]; loading: boolean; error: string | null }>(() => ({
    groups: myGroupsCache ?? [],
    loading: myGroupsCache == null,
    error: null
  }));
  useEffect(() => {
    let cancelled = false;
    if (myGroupsCache) {
      setState({ groups: myGroupsCache, loading: false, error: null });
      return;
    }
    (async () => {
      try {
        const groups = await fetchMyGroups();
        if (!cancelled) setState({ groups, loading: false, error: null });
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof ApiRequestError ? e.message : String(e);
        setState({ groups: [], loading: false, error: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

/**
 * ADR-0173: module-local cache of the caller's own account id (same pattern
 * as the my-groups cache above). The Personal pill uses it to attribute rows
 * owned by someone the caller oversees — plain "Personal" for own rows.
 */
let myAccountIdCache: string | null | undefined;
let myAccountIdInFlight: Promise<string | null> | null = null;

export function useMyAccountId(): string | null {
  const [accountId, setAccountId] = useState<string | null>(myAccountIdCache ?? null);
  useEffect(() => {
    if (myAccountIdCache !== undefined) return;
    if (!myAccountIdInFlight) {
      myAccountIdInFlight = apiFetch<{ user: { id: string } }>("/api/tenant/me")
        .then((r) => {
          myAccountIdCache = r.user.id;
          return myAccountIdCache;
        })
        .catch(() => {
          myAccountIdInFlight = null;
          return null;
        });
    }
    let alive = true;
    void myAccountIdInFlight.then((id) => {
      if (alive) setAccountId(id);
    });
    return () => {
      alive = false;
    };
  }, []);
  return accountId;
}
